/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useTranslation } from 'react-i18next'
import {
  qrCodeCardModel,
  qrCodeCardsPostPayloadModel,
  qrCodeCardsPutPayloadModel,
  QUERY
} from '../api/TableCardsManagementAPI.model'
import TableCardsManagementAPI from '../api/TableCardsManagementAPI'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'

export function useTableCardsQuery() {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const {
    data: tablesList,
    isLoading: isTablesListLoading,
    refetch: refetchTables
  } = useQuery({
    queryKey: [QUERY.TABLES],
    queryFn: TableCardsManagementAPI.getTables,
    staleTime: 0
  })

  const {
    data: tableCardsList,
    isLoading: isTableCardsListLoading,
    refetch: refetchTableCards
  } = useQuery({
    queryKey: [QUERY.QR_CODE_CARDS],
    queryFn: TableCardsManagementAPI.getQrCodeCards,
    staleTime: 0
  })

  const { mutate: postTables } = useMutation<void, ErrorModel, string>({
    mutationFn: async (numberOfTables: string) => {
      const response = await TableCardsManagementAPI.postTables(numberOfTables)
      return response.data
    },
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.POST_TABLES_SUCCESS.TITLE'),
        t('ALERTS.POST_TABLES_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.TABLES], exact: false })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_TABLES_ERROR.TITLE'),
        t('ALERTS.POST_TABLES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteTables } = useMutation<void, ErrorModel, string[]>({
    mutationFn: async (tableIds: string[]) => {
      const response = await TableCardsManagementAPI.deleteTables(tableIds)
      return response.data
    },
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_TABLES_SUCCESS.TITLE'),
        t('ALERTS.DELETE_TABLES_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.TABLES], exact: false })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_TABLES_ERROR.TITLE'),
        t('ALERTS.DELETE_TABLES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutateAsync: postTableCardImageAsync } = useMutation<
    { id: string; image: File },
    ErrorModel,
    { id: string; image: File }
  >({
    mutationFn: async ({ id, image }) => {
      const response =
        await TableCardsManagementAPI.postQrCodeCardBackgroundImage(id, image)
      return response.data
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_TABLE_CARD_IMAGE_UPLOAD_ERROR.TITLE'),
        t('ALERTS.POST_TABLE_CARD_IMAGE_UPLOAD_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: postTableCard } = useMutation<
    qrCodeCardModel,
    ErrorModel,
    qrCodeCardsPostPayloadModel
  >({
    mutationFn: async tableCardPayload => {
      const { backgroundImage, ...payload } = tableCardPayload
      const response = await TableCardsManagementAPI.postQrCodeCard(payload)
      return { ...response.data, backgroundImage }
    },
    onSuccess: async (data, variables) => {
      showSnackbar(
        t('ALERTS.POST_TABLE_CARD_DATA_SUCCESS.TITLE'),
        t('ALERTS.POST_TABLE_CARD_DATA_SUCCESS.DESCRIPTION'),
        'success'
      )
      if (variables.backgroundImage) {
        await postTableCardImageAsync({
          id: data.id,
          image: variables.backgroundImage
        })
      }
      queryClient.invalidateQueries({
        queryKey: [QUERY.QR_CODE_CARDS],
        exact: false
      })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_TABLE_CARD_DATA_ERROR.TITLE'),
        t('ALERTS.POST_TABLE_CARD_DATA_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: putTableCard } = useMutation<
    qrCodeCardModel,
    ErrorModel,
    qrCodeCardsPutPayloadModel
  >({
    mutationFn: async tableCardPayload => {
      const { backgroundImage, ...payload } = tableCardPayload
      const response = await TableCardsManagementAPI.putQrCodeCard(payload)
      return { ...response.data, backgroundImage }
    },
    onSuccess: async (_data, variables) => {
      showSnackbar(
        t('ALERTS.PUT_TABLE_CARD_DATA_SUCCESS.TITLE'),
        t('ALERTS.PUT_TABLE_CARD_DATA_SUCCESS.DESCRIPTION'),
        'success'
      )
      if (variables.backgroundImage) {
        await postTableCardImageAsync({
          id: variables.id,
          image: variables.backgroundImage
        })
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY.QR_CODE_CARDS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY.TABLES] })
      ])
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_TABLE_CARD_DATA_ERROR.TITLE'),
        t('ALERTS.PUT_TABLE_CARD_DATA_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteTableCards } = useMutation<
    string[],
    ErrorModel,
    string[]
  >({
    mutationFn: async ids => {
      const response = await TableCardsManagementAPI.deleteQrCodeCards(ids)
      return response.data
    },
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_TABLE_CARDS_SUCCESS.TITLE'),
        t('ALERTS.DELETE_TABLE_CARDS_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({
        queryKey: [QUERY.QR_CODE_CARDS],
        exact: false
      })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_TABLE_CARDS_ERROR.TITLE'),
        t('ALERTS.DELETE_TABLE_CARDS_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const clearTablesFromCards = async (selectedTableIds: string[]) => {
    if (!tableCardsList || !selectedTableIds.length) return

    const affectedCards = tableCardsList.filter(card =>
      card.tableIds?.some(id => selectedTableIds.includes(id))
    )

    if (!affectedCards.length) return

    const updatePayloads = affectedCards.map(card => ({
      ...card,
      tableIds: card.tableIds.filter(id => !selectedTableIds.includes(id))
    }))

    await Promise.all(
      updatePayloads.map(payload => {
        const { backgroundImage, ...restPayload } = payload
        return TableCardsManagementAPI.putQrCodeCard(restPayload)
          .then(() => {
            showSnackbar(
              t('ALERTS.CLEAR_TABLE_CARDS_FORM_SUCCESS.TITLE'),
              t('ALERTS.CLEAR_TABLE_CARDS_FORM_SUCCESS.DESCRIPTION'),
              'success'
            )
          })
          .catch(() => {
            showSnackbar(
              t('ALERTS.CLEAR_TABLE_CARDS_FORM_ERROR.TITLE'),
              t('ALERTS.CLEAR_TABLE_CARDS_FORM_ERROR.DESCRIPTION'),
              'error'
            )
          })
      })
    )

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERY.QR_CODE_CARDS] }),
      queryClient.invalidateQueries({ queryKey: [QUERY.TABLES] })
    ])
  }

  return {
    tablesList,
    isTablesListLoading,
    refetchTables,
    tableCardsList,
    isTableCardsListLoading,
    refetchTableCards,
    postTables,
    deleteTables,
    postTableCard,
    putTableCard,
    deleteTableCards,
    clearTablesFromCards
  }
}
